window.customElements.define(
  'custom-variant-picker-toggle',
  class CustomVariantPickerToggle extends window.HTMLElement {
    connectedCallback() {
      this.initDOMElements();
      this.initEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    /* -------------------- INIT -------------------- */

    initDOMElements() {
      this.form = this.querySelector('form');
      this.addToCartPrice = this.querySelector('#add-to-cart-price');
      this.addToCartCompareAtPrice = this.querySelector('#add-to-cart-compare-at-price');
      this.errorMessage = this.querySelector('#error-message');
      this.addToCartButton = this.querySelector('#add-to-cart');
      this.notifyMeButton = this.querySelector('#notify-me-button');
      this.oneTimeSwatch = this.querySelector('#swatch-item-one-time-purchase');
      this.subscribeSwatch = this.querySelector('#swatch-item-subscribe-and-save');

      this.variants = this.form ? this.form.querySelectorAll('.custom-variant-input') : [];
      this.savings = this.querySelectorAll('#savings');
      this.savingsWrapper = this.querySelectorAll('#savings-wrapper');
      this.optionUnitPriceWrapper = this.querySelectorAll('#option-unit-price-wrapper');
      this.optionUnitPrices = this.querySelectorAll('#option-unit-price');
      this.pauseOrCancelAnytimeText = this.querySelector('#pause-or-cancel-anytime');

      this._boundHandleChange = this.handleChange.bind(this);
      this._boundHandleSubmit = this.handleAddToCartSubmit.bind(this);
      this._boundOneTimeClick = this.handleChange.bind(this);
      this._boundSubscribeClick = this.handleChange.bind(this);
      this._boundStickyListener = this.handleStickyAddToCartChange.bind(this);
    }

    initEventListeners() {
      if (this.form) {
        this.form.addEventListener('change', this._boundHandleChange);
        this.form.addEventListener('submit', this._boundHandleSubmit);
      }

      if (this.oneTimeSwatch) this.oneTimeSwatch.addEventListener('click', this._boundOneTimeClick);
      if (this.subscribeSwatch) this.subscribeSwatch.addEventListener('click', this._boundSubscribeClick);

      window.addEventListener('custom-sticky-add-to-cart:change', this._boundStickyListener);
    }

    removeEventListeners() {
      if (this.form) {
        this.form.removeEventListener('change', this._boundHandleChange);
        this.form.removeEventListener('submit', this._boundHandleSubmit);
      }

      if (this.oneTimeSwatch) this.oneTimeSwatch.removeEventListener('click', this._boundOneTimeClick);
      if (this.subscribeSwatch) this.subscribeSwatch.removeEventListener('click', this._boundSubscribeClick);

      window.removeEventListener('custom-sticky-add-to-cart:change', this._boundStickyListener);
    }

    /* -------------------- EVENTS -------------------- */

    handleStickyAddToCartChange(event) {
      const { variant } = event.detail;
      if (!variant.variantId) return;

      const variantInput = this.form.querySelector(
        `input[type="radio"][value="${variant.variantId}"]`
      );

      if (!variantInput) return;

      variantInput.checked = true;

      const currentVariantData = this.getCurrentVariantData();
      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
    }

    handleChange(event) {
      const currentVariantData = this.getCurrentVariantData();

      this.handleUpdateUI(currentVariantData, event.target);
      this.handleUpdateUrl(currentVariantData);

      this.dispatchEvent(
        new CustomEvent('custom-variant-picker:change', {
          detail: {
            variant: {
              available: currentVariantData.available,
              variantId: currentVariantData.variantId,
              sellingPlanId: currentVariantData.sellingPlanId,
              sellingPlanIdGel: currentVariantData.sellingPlanIdGel,
              price: currentVariantData.variantPrice,
              compareAtPrice: currentVariantData.variantCompareAtPrice,
              subscriptionPrice: currentVariantData.variantSubscriptionPrice,
            },
            element: event.target,
          },
          bubbles: true,
        })
      );
    }

    async handleAddToCartSubmit(event) {
      event.preventDefault();

      const currentVariantData = this.getCurrentVariantData();
      const cartItems = this.buildCartItems(currentVariantData);

      try {
        this.setLoadingState(true);
        await this.addToCart(cartItems);
        this.dispatchCartRefresh();
      } catch (error) {
        this.handleAddToCartError(error);
      } finally {
        this.setLoadingState(false);
      }
    }

    /* -------------------- CART LOGIC -------------------- */

    buildCartItems(currentVariantData) {
      const paymentType = this.getActivePaymentOption();

      const mainSellingPlan =
        paymentType === 'subscription' ? currentVariantData.sellingPlanId : '';

      const gelSellingPlan =
        paymentType === 'subscription' ? currentVariantData.sellingPlanIdGel : '';

      // Subscription bundle
      if (
        paymentType === 'subscription' &&
        (currentVariantData.variantIdSub || currentVariantData.variantId)
      ) {
        const items = [];

        // MAIN PRODUCT (NO selling plan)
        items.push({
          id: currentVariantData.variantIdSub || currentVariantData.variantId,
          quantity: 1,
        });

        // GEL PRODUCT (OWN selling plan)
        if (currentVariantData.variantIdSubGel) {
          items.push({
            id: currentVariantData.variantIdSubGel,
            quantity: 1,
            ...(gelSellingPlan ? { selling_plan: gelSellingPlan } : {}),
          });
        }

        return items;
      }

      // Default single item
      const item = {
        id: currentVariantData.variantId,
        quantity: 1,
      };

      if (mainSellingPlan) {
        item.selling_plan = mainSellingPlan;
      }

      return [item];
    }

    async addToCart(items) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Add to cart failed');
      }

      return response.json();
    }

    dispatchCartRefresh() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('variant:add'));
    }

    /* -------------------- UI -------------------- */

    handleUpdateUI(currentVariantData, eventTarget = null) {
      this.updateButtonVisibility(currentVariantData);
      this.updatePriceDisplay();
      if (eventTarget) this.handleSwatchClick(eventTarget);
    }

    updateButtonVisibility(currentVariantData) {
      this.addToCartButton?.classList.toggle('hidden', !currentVariantData.available);
      this.notifyMeButton?.classList.toggle('hidden', currentVariantData.available);
    }

    updatePriceDisplay() {
      const paymentType = this.getActivePaymentOption();
      const activeVariantInput = this.getActiveVariantInput();
      if (!activeVariantInput) return;

      if (paymentType === 'one-time') {
        this.updateOneTimePrices(activeVariantInput);
        this.pauseOrCancelAnytimeText?.classList.add('hidden');
      } else {
        this.updateSubscriptionPrices(activeVariantInput);
        this.pauseOrCancelAnytimeText?.classList.remove('hidden');
      }
    }

    updateOneTimePrices(input) {
      this.setMainPrice(input.dataset.variantPrice);
      this.setCompareAtPrice(
        input.dataset.variantCompareAtPrice,
        input.dataset.variantPrice
      );
    }

    updateSubscriptionPrices(input) {
      this.setMainPrice(input.dataset.variantSubscriptionPrice);
      this.setCompareAtPrice(
        input.dataset.variantCompareAtPrice,
        input.dataset.variantSubscriptionPrice
      );
    }

    setMainPrice(price) {
      if (this.addToCartPrice) this.addToCartPrice.textContent = price;
    }

    setCompareAtPrice(compareAtPrice, mainPrice) {
      if (!this.addToCartCompareAtPrice) return;

      this.addToCartCompareAtPrice.textContent = compareAtPrice;
      this.addToCartCompareAtPrice.classList.toggle(
        'hidden',
        compareAtPrice === mainPrice
      );
    }

    handleSwatchClick(target) {
      if (target.id === 'swatch-item-one-time-purchase') {
        this.activateOneTimeSwatch();
      } else if (target.id === 'swatch-item-subscribe-and-save') {
        this.activateSubscriptionSwatch();
      }
    }

    activateOneTimeSwatch() {
      this.updateSwatchState('one-time');
      this.updateVariantOptions('one-time');
      this.updatePriceDisplay();
    }

    activateSubscriptionSwatch() {
      this.updateSwatchState('subscription');
      this.updateVariantOptions('subscription');
      this.updatePriceDisplay();
    }

    updateSwatchState(type) {
      this.oneTimeSwatch?.classList.toggle('active', type === 'one-time');
      this.subscribeSwatch?.classList.toggle('active', type === 'subscription');
    }

    updateVariantOptions(type) {
      this.form
        ?.querySelectorAll('input.custom-variant-input')
        .forEach((input, index) => {
          if (type === 'one-time') {
            this.setOptionUnitPrice(index, input.dataset.variantUnitPrice);
            this.setOptionSavings(index, input.dataset.variantSavings);
          } else {
            this.setOptionUnitPrice(index, input.dataset.variantSubscriptionUnitPrice);
            this.setOptionSavings(index, input.dataset.variantSubscriptionSavings);
          }
        });
    }

    setOptionUnitPrice(index, price) {
      if (this.optionUnitPrices[index]) {
        this.optionUnitPrices[index].textContent = price;
      }
    }

    setOptionSavings(index, savings) {
      if (!savings) {
        this.savingsWrapper[index]?.classList.add('hidden');
        this.optionUnitPriceWrapper[index]?.classList.add('hidden');
        return;
      }

      this.savingsWrapper[index]?.classList.remove('hidden');
      this.optionUnitPriceWrapper[index]?.classList.remove('hidden');

      if (this.savings[index]) {
        this.savings[index].textContent = savings;
      }
    }

    /* -------------------- HELPERS -------------------- */

    getActiveVariantInput() {
      return this.form?.querySelector('input[type="radio"]:checked') || null;
    }

    handleUpdateUrl(data) {
      if (!data.variantId) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', data.variantId);
      window.history.replaceState({}, '', url);
    }

    getCurrentVariantData() {
      const currentVariant = this.getActiveVariantInput();

      if (!currentVariant) {
        return {
          variantId: null,
          sellingPlanId: '',
          sellingPlanIdGel: '',
          variantPrice: null,
          variantCompareAtPrice: null,
          variantSubscriptionPrice: null,
          available: false,
          variantIdSub: null,
          variantIdSubGel: null,
        };
      }

      const {
        available,
        variantId,
        sellingPlanId,
        sellingPlanIdGel,
        variantPrice,
        variantCompareAtPrice,
        variantSubscriptionPrice,
        variantIdSub,
        variantIdSubGel,
      } = currentVariant.dataset;

      return {
        variantId,
        sellingPlanId,
        sellingPlanIdGel: sellingPlanIdGel || '',
        variantPrice,
        variantCompareAtPrice,
        variantSubscriptionPrice,
        available,
        variantIdSub: variantIdSub || null,
        variantIdSubGel: variantIdSubGel || null,
      };
    }

    getActivePaymentOption() {
      if (this.subscribeSwatch?.classList.contains('active')) return 'subscription';
      return 'one-time';
    }

    setLoadingState(isLoading) {
      this.form?.classList.toggle('is-loading', isLoading);
    }

    handleAddToCartError(error) {
      console.error(error);
      this.showErrorMessage(error.message || 'Failed to add to cart');
    }

    showErrorMessage(message) {
      if (!this.errorMessage) return;

      this.errorMessage.textContent = message;
      this.errorMessage.classList.remove('hidden');

      setTimeout(() => {
        this.errorMessage.classList.add('hidden');
      }, 3000);
    }
  }
);
